import moment from 'moment';
import { isNumber } from "./utils";
import Logger from "../functions/logging";
import { SEL_CODES } from "../constants/selCodes";
import * as persistent from './persistent';

const VIDEO_RESEARCH_ENV = 'inVideo';

export const hasUserJoined = (study, user) => {
	if (study && study.userData) {
		return study.userData.status === 1;
	}

	return false;
};

export const userHasInList = (study, user) => {
	if (study && study.userData) {
		return study.userData.mylist === 1;
	}

	return false;
};

export const hasUserCompleted = (study, user) => {
	let completed = false;
	if (study) {
		if (study.userData && study.userData.complete === 1) {
			completed = true;
		}
	
		if (study._id) {
			const recentResult = getRecentStudyResults(study._id);
			if (recentResult && recentResult.status === "completed") {
				completed = true;
			}
		}
	}

	return completed;
};

export const isUserScreened = (study, user) => {
	let screened = false;
	if (study) {
		if (study.userData && study.userData.screened === 1) {
			screened = true;
		}

		if (study._id) {
			const recentResult = getRecentStudyResults(study._id);
			if (recentResult && recentResult.status === "screened") {
				screened = true;
			}
		}
	}

	return screened;
};

export const inProgress = (study, user) => {
	return false;
};

export const hasMpaaRating = (episode) => {
	if (episode && episode.mpaa && episode.mpaa.label) {
		switch (episode.mpaa.label) {
			case "tv-g":
			case "tv-pg":
			case "tv-14":
			case "tv-ma":
			case "g":
			case "pg":
			case "pg-13":
			case "r":
				return true;
			default:
				return false;
		}
	}
	return false;
};

export const isPastEndDate = (study) => {
	let finished = false;

	if (study && study.end_date) {
		const endDate = moment(study.end_date, "YYYY-MM-DD");
		const currentDate = moment();
		if (currentDate.isAfter(endDate, 'day')) {
			finished = true;
		}
	}

	return finished;
};

const RECENT_STUDIES_SESSION_KEY = "studyResults";

export const getStudyResultsFromSession = () => {
	const currentValuesStr = persistent.getPersistentValue(RECENT_STUDIES_SESSION_KEY);
	if (currentValuesStr) {
		return JSON.parse(currentValuesStr);
	}
	return [];
};

export const setStudyResultsFromSession = (results) => {
	persistent.setPersistentValue(RECENT_STUDIES_SESSION_KEY, JSON.stringify(results));
};

export const clearExpiredStudyResults = () => {
	let updatedValues = [];

	const currentValues = getStudyResultsFromSession();
	if (currentValues) {
		currentValues.forEach(value => {
			if (moment().diff(value.timestamp, 'hours') < 24) {
				updatedValues.push(value);
			}
		});
	}

	setStudyResultsFromSession(updatedValues);
};

export const getRecentStudyResults = (groupId) => {
	const currentValues = getStudyResultsFromSession();
	if (currentValues) {
		for (let i = 0; i < currentValues.length; i++) {
			const value = currentValues[i];
			if (value.groupId === groupId.valueOf()) {
				return value;
			}
		}
	}
	return null;
};

export const addRecentStudyResults = (groupId, status) => {
	clearExpiredStudyResults();

	if (groupId && status) {
		if (!getRecentStudyResults(groupId)) {
			const values = getStudyResultsFromSession();
			values.push({
				groupId: groupId,
				status: status,
				timestamp: moment().format('YYYY-MM-DD HH:mm:ss.SS Z')
			});

			setStudyResultsFromSession(values);
		}
	}
};

export const isModerated = (targetParams, fetchedGroup) => {
	Logger.log('isModerated()', targetParams, fetchedGroup);

	if (!targetParams || !fetchedGroup || !fetchedGroup.panel_segment) {
		return false;
	}

	const psIndex = isNumber(targetParams.ps) ? Number(targetParams.ps) : -1;
	const panelSegment = -1 < psIndex && psIndex < fetchedGroup.panel_segment.length ? fetchedGroup.panel_segment[psIndex] : null;

	Logger.log('isModerated() result', psIndex, panelSegment && panelSegment.moderated);
	return panelSegment && panelSegment.moderated;
};

export const isVideoStudy = (fetchedGroup) => {
	return fetchedGroup && fetchedGroup.research_env === VIDEO_RESEARCH_ENV;
};

export const getSELCodes = (targetParams) => {
	return targetParams && targetParams.sel ? SEL_CODES[targetParams.sel] : null;
};

export const getCompletedTimecode = (fetchedGroup, fetchedEpisode, currentUser) => { 
	let timecode = 0;
	if (!fetchedGroup 
		|| !fetchedEpisode
		|| !fetchedEpisode.duration
		|| !currentUser 
		|| !currentUser.subscriptions 
		|| !currentUser.subscriptions.group
		|| !currentUser.subscriptions.group.length
	) return timecode;

	for (let i = 0; i < currentUser.subscriptions.group.length; i++) {
		const group = currentUser.subscriptions.group[i];
		if (group.group_id.valueOf() === fetchedGroup._id.valueOf() && group.completion) {
			timecode = fetchedEpisode.duration * group.completion;
		}
	}

	return timecode;
};

export const isAmazonPartnerStudy = (targetParams) => { 
	const AmazonPartners = ["alida", "qualtrics"];
	/**
	 * The parameter `vo` is added here to ensure that only "video only" studies are used in mobile web
	 * Once we're able to use survey only in mobile web environment this parameter will be removed from
	 * the method
	 */
	const isAmazonPartner = targetParams
		&& AmazonPartners.includes(targetParams.partner)
		&& targetParams.vo === "1";
	return isAmazonPartner;
};

export const isDesktopEnabledStudy = (panelSegment) => {

	const studyDevices = panelSegment.study_devices;

	//When there are no devices selected for a study,
	//enable it to desktop
	if (!studyDevices || studyDevices.length === 0) {
		return true;
	}

	return studyDevices.some((studyDevice) => studyDevice === 'desktop_web');
};

export const isMobileWebEnabledStudy = (panelSegment) => {
	const studyDevices = panelSegment.study_devices;
	if (studyDevices) {
		return studyDevices.some((studyDevice) => studyDevice === 'mobile_web');
	}
	return false;
};


// WEBPACK FOOTER //
// ./src/functions/study.js